import { useTranslation } from '@rhino-project/core/i18n';
/* eslint @eslint-react/no-array-index-key: "error" */
import {
  createContext,
  Fragment,
  use,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Button,
  Card,
  Drawer,
  Label,
  TextArea,
  TextField
} from '@heroui/react';
import ReactMarkdown from 'react-markdown';
import {
  useGlobalComponent,
  useRhinoChat,
  type ChatMessageData,
  type RhinoChatOptions
} from '@rhino-project/core/hooks';

type ChatState = ReturnType<typeof useRhinoChat>;

export interface ChatProps extends RhinoChatOptions {
  title?: string;
  description?: string;
  suggestions?: string[];
  onNavigate?: (target: ChatNavigationTarget) => void | Promise<void>;
}

export interface ChatNavigationTarget {
  kind: 'record' | 'list';
  resource: string;
  label: string;
  id?: string;
  filter?: Record<string, unknown>;
  search?: string;
  order?: string;
}

const ChatNavigationContext = createContext<ChatProps['onNavigate']>(undefined);

export const ChatNavigationBase = ({ output }: { output: unknown }) => {
  const onNavigate = use(ChatNavigationContext);
  if (!onNavigate) return null;
  let parsed = output;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  if (
    !parsed ||
    typeof parsed !== 'object' ||
    !('navigation' in parsed) ||
    !Array.isArray(parsed.navigation)
  )
    return null;
  const targets = parsed.navigation.filter(
    (target): target is ChatNavigationTarget =>
      target &&
      typeof target === 'object' &&
      (target.kind === 'record' || target.kind === 'list') &&
      typeof target.resource === 'string' &&
      typeof target.label === 'string' &&
      (target.kind !== 'record' || typeof target.id === 'string')
  );
  return (
    <div className="flex flex-wrap gap-2">
      {targets.map((target) => (
        <Button
          key={`${target.kind}:${target.resource}:${target.id || 'results'}`}
          variant="secondary"
          size="sm"
          onPress={() => {
            void onNavigate(target);
          }}
        >
          {target.label}
        </Button>
      ))}
    </div>
  );
};
export const ChatNavigation = (props: { output: unknown }) =>
  useGlobalComponent('ChatNavigation', ChatNavigationBase, props);

export const ChatMessageBase = ({ message }: { message: ChatMessageData }) => {
  const { t } = useTranslation();
  return (
    <article
      aria-label={
        message.role === 'user'
          ? t('rhino.frontend.messages.you')
          : t('rhino.frontend.messages.assistant')
      }
      className="space-y-2"
    >
      <p className="text-sm font-semibold">
        {message.role === 'user'
          ? t('rhino.frontend.messages.you')
          : t('rhino.frontend.messages.assistant')}
      </p>
      {message.parts.map((part, index) => {
        if (part.type === 'text')
          // Text parts have no IDs; streaming appends parts without reordering them.
          // eslint-disable-next-line @eslint-react/no-array-index-key
          return <ChatMessageContent key={index} text={part.content} />;
        if (part.type === 'tool-call')
          return (
            <div key={part.id}>
              <ChatToolCall
                name={part.name}
                state={part.state}
                output={part.output}
              />
              {part.state === 'complete' && (
                <ChatNavigation output={part.output} />
              )}
            </div>
          );
        return null;
      })}
    </article>
  );
};
export const ChatMessage = (props: { message: ChatMessageData }) =>
  useGlobalComponent('ChatMessage', ChatMessageBase, props);

export const ChatMessageContentBase = ({ text }: { text: string }) => (
  <div className="space-y-2 break-words [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_pre]:overflow-x-auto">
    <ReactMarkdown skipHtml disallowedElements={['img']}>
      {text}
    </ReactMarkdown>
  </div>
);
export const ChatMessageContent = (props: { text: string }) =>
  useGlobalComponent('ChatMessageContent', ChatMessageContentBase, props);

export const ChatToolCallBase = ({
  name,
  state,
  output
}: {
  name: string;
  state: string;
  output?: unknown;
}) => {
  const { t } = useTranslation();
  let result = output;
  if (typeof result === 'string') {
    try {
      result = JSON.parse(result);
    } catch {
      /* Plain text tool result. */
    }
  }
  const failed =
    state === 'error' ||
    (result && typeof result === 'object' && 'error' in result);
  const labels: Record<string, [string, string, string]> = {
    list_records: [
      t('rhino.frontend.messages.chat_reading_records'),
      t('rhino.frontend.messages.chat_records_checked'),
      t('rhino.frontend.messages.chat_read_failed')
    ],
    open_record: [
      t('rhino.frontend.messages.chat_opening_record'),
      t('rhino.frontend.messages.chat_navigation_processed'),
      t('rhino.frontend.messages.chat_open_failed')
    ],
    show_results: [
      t('rhino.frontend.messages.chat_opening_results'),
      t('rhino.frontend.messages.chat_navigation_processed'),
      t('rhino.frontend.messages.chat_results_failed')
    ],
    read_page_context: [
      t('rhino.frontend.messages.chat_checking_page'),
      t('rhino.frontend.messages.chat_page_checked'),
      t('rhino.frontend.messages.chat_context_failed')
    ]
  };
  const [pending, complete, error] = labels[name] ?? [
    t('rhino.frontend.messages.chat_working'),
    t('rhino.frontend.messages.chat_action_processed'),
    t('rhino.frontend.messages.chat_action_failed')
  ];
  const count =
    name === 'list_records' &&
    result &&
    typeof result === 'object' &&
    'total' in result &&
    typeof result.total === 'number' &&
    'results' in result &&
    Array.isArray(result.results)
      ? t('rhino.frontend.messages.chat_record_count', {
          count: result.results.length,
          total: result.total
        })
      : complete;
  return (
    <p className="text-sm text-muted" role="status">
      {failed ? error : state === 'complete' ? count : pending}
    </p>
  );
};
export const ChatToolCall = (props: {
  name: string;
  state: string;
  output?: unknown;
}) => useGlobalComponent('ChatToolCall', ChatToolCallBase, props);

export const ChatMessagesBase = ({
  messages
}: {
  messages: ChatMessageData[];
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const followRef = useRef(true);
  useEffect(() => {
    if (followRef.current && containerRef.current)
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [messages]);
  return (
    <div
      ref={containerRef}
      role="log"
      aria-label={t('rhino.frontend.messages.conversation')}
      aria-live="polite"
      aria-relevant="additions text"
      className="max-h-96 space-y-5 overflow-y-auto"
      onScroll={() => {
        const element = containerRef.current;
        if (element)
          followRef.current =
            element.scrollHeight - element.scrollTop - element.clientHeight <
            80;
      }}
    >
      {messages.map((message) => (
        <ChatMessage key={message.id} message={message} />
      ))}
    </div>
  );
};
export const ChatMessages = (props: { messages: ChatMessageData[] }) =>
  useGlobalComponent('ChatMessages', ChatMessagesBase, props);

export const ChatEmptyBase = ({
  suggestions = [],
  onSend
}: {
  suggestions?: string[];
  onSend: (text: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <p className="text-muted">
        {t('rhino.frontend.messages.ask_a_question_to_start_a_conversation')}
      </p>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((suggestion) => (
          <Button
            key={suggestion}
            variant="secondary"
            onPress={() => onSend(suggestion)}
          >
            {suggestion}
          </Button>
        ))}
      </div>
    </div>
  );
};
export const ChatEmpty = (props: {
  suggestions?: string[];
  onSend: (text: string) => void;
}) => useGlobalComponent('ChatEmpty', ChatEmptyBase, props);

export const ChatErrorBase = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation();
  return (
    <div role="alert" className="flex items-center gap-3">
      <p>
        {t(
          'rhino.frontend.messages.the_assistant_could_not_finish_please_try_again'
        )}
      </p>
      <Button variant="secondary" onPress={onRetry}>
        {t('rhino.frontend.messages.retry')}
      </Button>
    </div>
  );
};
export const ChatError = (props: { onRetry: () => void }) =>
  useGlobalComponent('ChatError', ChatErrorBase, props);

export interface ChatStatusProps {
  isLoading: boolean;
  progress?: string | null;
}

export const ChatStatusBase = ({ isLoading, progress }: ChatStatusProps) => {
  const { t } = useTranslation();
  const labels: Record<string, string> = {
    preparing_response: t('rhino.frontend.messages.chat_preparing_response'),
    reading_records: t('rhino.frontend.messages.chat_reading_records'),
    preparing_browser_action: t(
      'rhino.frontend.messages.chat_preparing_action'
    ),
    writing_response: t('rhino.frontend.messages.chat_writing_response'),
    open_record: t('rhino.frontend.messages.chat_opening_record'),
    show_results: t('rhino.frontend.messages.chat_opening_results'),
    read_page_context: t('rhino.frontend.messages.chat_checking_page')
  };
  return isLoading ? (
    <p role="status" className="text-sm text-muted">
      {labels[progress ?? ''] ?? t('rhino.frontend.messages.chat_working')}
    </p>
  ) : null;
};
export const ChatStatus = (props: ChatStatusProps) =>
  useGlobalComponent('ChatStatus', ChatStatusBase, props);

export const ChatComposerBase = ({
  isLoading,
  onSend,
  onStop
}: {
  isLoading: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const submit = () => {
    if (isLoading || !value.trim()) return;
    onSend(value.trim());
    setValue('');
  };
  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <TextField value={value} onChange={setValue} isDisabled={isLoading}>
        <Label>{t('rhino.frontend.messages.message')}</Label>
        <TextArea
          rows={3}
          maxLength={8000}
          placeholder={t('rhino.frontend.messages.ask_a_question')}
          onKeyDown={(event) => {
            if (
              event.key === 'Enter' &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              submit();
            }
          }}
        />
      </TextField>
      <div className="flex justify-end">
        {isLoading ? (
          <Button variant="secondary" onPress={onStop}>
            {t('rhino.frontend.messages.stop')}
          </Button>
        ) : (
          <Button type="submit" isDisabled={!value.trim()}>
            {t('rhino.frontend.messages.send')}
          </Button>
        )}
      </div>
    </form>
  );
};
export const ChatComposer = (props: {
  isLoading: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
}) => useGlobalComponent('ChatComposer', ChatComposerBase, props);

export const ChatHeaderBase = ({
  title,
  description,
  onClear,
  isLoading
}: {
  title: string;
  description?: string;
  onClear: () => void;
  isLoading: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Card.Header>
      <div className="flex items-center justify-between gap-3">
        <Card.Title>{title}</Card.Title>
        <Button variant="tertiary" isDisabled={isLoading} onPress={onClear}>
          {t('rhino.frontend.messages.new_chat')}
        </Button>
      </div>
      {description && <Card.Description>{description}</Card.Description>}
    </Card.Header>
  );
};
export const ChatHeader = (props: {
  title: string;
  description?: string;
  onClear: () => void;
  isLoading: boolean;
}) => useGlobalComponent('ChatHeader', ChatHeaderBase, props);

export const ChatPanelBase = ({
  chat,
  title,
  description,
  suggestions,
  onNavigate
}: ChatProps & { chat: ChatState }) => {
  const { t } = useTranslation();
  const send = (text: string) => {
    void chat.sendMessage(text);
  };
  return (
    <ChatNavigationContext value={onNavigate}>
      <Card>
        <ChatHeader
          title={title ?? t('rhino.frontend.messages.chat_title')}
          description={description}
          onClear={chat.clear}
          isLoading={chat.isLoading}
        />
        <Card.Content className="space-y-4">
          {chat.messages.length ? (
            <ChatMessages messages={chat.messages} />
          ) : (
            <ChatEmpty suggestions={suggestions} onSend={send} />
          )}
          <ChatStatus isLoading={chat.isLoading} progress={chat.progress} />
          {chat.error && (
            <ChatError
              onRetry={() => {
                void chat.reload();
              }}
            />
          )}
          <ChatComposer
            isLoading={chat.isLoading}
            onSend={send}
            onStop={chat.stop}
          />
          <p className="text-xs text-muted">
            {t('rhino.frontend.messages.chat_notice')}
          </p>
        </Card.Content>
      </Card>
    </ChatNavigationContext>
  );
};
export const ChatPanel = (props: ChatProps & { chat: ChatState }) =>
  useGlobalComponent('ChatPanel', ChatPanelBase, props);

export const ChatBase = (props: ChatProps) => {
  const chat = useRhinoChat(props);
  return <ChatPanel {...props} chat={chat} />;
};

export interface ChatSheetProps extends ChatProps {
  triggerLabel?: string;
}

export const ChatSheetBase = ({ triggerLabel, ...props }: ChatSheetProps) => {
  const { t } = useTranslation();
  // Keep the conversation alive while the drawer content is unmounted.
  const chat = useRhinoChat(props);
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Drawer>
      <Button variant="secondary" onPress={() => setIsOpen(true)}>
        {triggerLabel ?? t('rhino.frontend.messages.chat_ask_ai')}
      </Button>
      <Drawer.Backdrop isOpen={isOpen} onOpenChange={setIsOpen}>
        <Drawer.Content placement="right">
          <Drawer.Dialog
            aria-label={
              props.title || t('rhino.frontend.messages.chat_heading')
            }
            className="h-full w-[32rem] max-w-full"
          >
            <Drawer.CloseTrigger />
            <Drawer.Header>
              <Drawer.Heading>
                {props.title || t('rhino.frontend.messages.chat_heading')}
              </Drawer.Heading>
            </Drawer.Header>
            <Drawer.Body>
              <ChatPanel
                {...props}
                chat={chat}
                onNavigate={
                  props.onNavigate
                    ? async (target) => {
                        await props.onNavigate?.(target);
                        setIsOpen(false);
                      }
                    : undefined
                }
              />
            </Drawer.Body>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
};
export const ChatSheet = (props: ChatSheetProps) => (
  <Fragment key={`${props.assistant}:${props.ownerId}`}>
    {useGlobalComponent('ChatSheet', ChatSheetBase, props)}
  </Fragment>
);

export const Chat = (props: ChatProps) => (
  <Fragment key={`${props.assistant}:${props.ownerId}`}>
    {useGlobalComponent('Chat', ChatBase, props)}
  </Fragment>
);
